import { api, type MeasurementOutcome } from './api'
import { trackDataFastGoal } from './analytics'
import { hasConsent } from './consent/storage'
import { sendGoogleAnalyticsEvent, sendVerifiedStripeTrialConversion } from './consent/googleTag'
import { isWebMeasurementAllowed } from './measurementPolicy'

const SENT_KEY = 'optioneer_sent_funnel_outcomes_v1'
const sent = new Set<string>()
let syncing: Promise<void> | null = null
const names = {
  signup_completed: { ga: 'sign_up', datafast: 'signup_completed' },
  checkout_started: { ga: 'checkout_started', datafast: 'checkout_started' },
  trial_activated: { ga: 'trial_activated', datafast: 'trial_activated' },
  subscription_first_payment_succeeded: {
    ga: 'subscription_first_payment_succeeded',
    datafast: 'subscription_first_payment_succeeded',
  },
} as const

function persistedIds(): string[] {
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(SENT_KEY) || '[]')
    return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

function sendOnce(vendor: string, id: string, sendEvent: () => boolean) {
  const key = `${vendor}:${id}`
  if (sent.has(key) || persistedIds().includes(key) || !sendEvent()) return
  sent.add(key)
  try {
    window.localStorage.setItem(SENT_KEY, JSON.stringify([...persistedIds(), key].slice(-500)))
  } catch {
    /* In-memory dedupe still covers concurrent renders in this page. */
  }
}

/** Consume only current, trusted outcomes exposed by the existing operational funnel. */
export function relayMeasurementOutcomes(outcomes: MeasurementOutcome[]): void {
  if (!isWebMeasurementAllowed()) return
  for (const outcome of outcomes) {
    if (!/^[a-f0-9]{64}$/.test(outcome.id) || !Object.hasOwn(names, outcome.event_name)) continue
    const event = names[outcome.event_name]
    if (hasConsent('analytics')) {
      sendOnce('ga', outcome.id, () => sendGoogleAnalyticsEvent(event.ga, { event_id: outcome.id }))
      sendOnce('datafast', outcome.id, () => trackDataFastGoal(event.datafast))
    }
    if (outcome.event_name === 'trial_activated' && hasConsent('marketing')) {
      sendVerifiedStripeTrialConversion(outcome.id)
    }
  }
}

export function syncWebMeasurementOutcomes(
  stillCurrent: () => boolean = () => true,
  refreshAfterCurrent = false
): Promise<void> {
  if (!isWebMeasurementAllowed() || (!hasConsent('analytics') && !hasConsent('marketing'))) {
    return Promise.resolve()
  }
  if (syncing)
    return refreshAfterCurrent
      ? syncing.then(() => syncWebMeasurementOutcomes(stillCurrent))
      : syncing
  syncing = api.funnel
    .getMeasurementOutcomes()
    .then(({ events }) => {
      if (stillCurrent()) relayMeasurementOutcomes(events)
    })
    .catch(() => {
      // Measurement must never block auth, checkout, or access finalization; retry on the next visit.
    })
    .finally(() => {
      syncing = null
    })
  return syncing
}

export function resetFunnelMeasurementForTests() {
  sent.clear()
  syncing = null
}
